// fetchChatroomSearch.js
'use strict';
import debounce from 'lodash/debounce';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import getMeData from '../selector/getMeData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';

import {
  ADD_MESSAGES,
  SET_LIST_ITEMS,
  ADD_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const debounceFunctionMap = {};

/**
 * Fetch chatroom search
 * @kind action
 * @param {string} {chatroomId} - chat id.
 * @param {string} {query} - query.
 * @param {number} {page} - page.
 * @param {number} {limit} - page size.
 * @return {Promise} Action promise.
 */
const fetchChatroomSearch = payload => async dispatch => {
  const { chatroomId } = payload;
  if (!debounceFunctionMap[chatroomId]) {
    debounceFunctionMap[chatroomId] = debounce(
      payload => dispatch(fetchChatroomSearchCore(payload)),
      500 // TODO: remote config
    );
  }
  return debounceFunctionMap[chatroomId](payload);
};

export const fetchChatroomSearchCore =
  ({ chatroomId, query, page = 1, limit = 10 }) =>
  async (dispatch, getState) => {
    const listSelectPath = ['chatroom', chatroomId, 'search'];
    const networkingSelectPath = ['chatroom', chatroomId, 'search', page];
    const token = getMeData(getState(), 'token');
    if (!token || !query || !chatroomId) {
      return dispatch({ type: '' });
    }

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const url = getResourceUrl({ endpoint: `/chat/${chatroomId}/search` });
    url.searchParams.set('q', query);
    url.searchParams.set('limit', limit);
    url.searchParams.set('page', page);

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const payload = await response.json();
      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const links = parseLinkHeader(response.headers.get('Link'));
      const nextPage = links && links.next && parseInt(links.next.page, 10);
      const lastPage = links && links.last && parseInt(links.last.page, 10);
      const itemIds = payload.map(item => item.id);
      const messages = objectifyArrayById({
        array: payload.map(item => {
          const message = {
            ...item,
            postedAtUnix: item.postedAt,
            senderId: item.sender,
          };
          delete message.postedAt;
          delete message.sender;
          return message;
        }),
      });

      dispatch({
        type: ADD_MESSAGES,
        payload: {
          messages,
        },
      });
      dispatch({
        type: page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: listSelectPath,
          totalCount,
          nextPage,
          lastPage,
          itemIds,
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchChatroomSearch;
